import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Layers,
  ArrowRight,
  RefreshCw,
  FolderOpen,
  LayoutGrid,
  UserPlus,
  BarChart3,
  Users,
  Search,
  CalendarRange,
} from "lucide-react";
import {
  LogbookDiet,
  useGetDietsQuery,
} from "../../logbook-diets/api/logbookDietApi";
import { useGetDietCategoryCountsQuery } from "../api/probationerApplicationApi";
import AccessGate from "../../../components/AccessGate";
import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";

/**
 * Entry (probationer) applications — the admin landing screen.
 *
 * Deliberately the same screen as AdminUpgradeApplicationsPage: a hub you pick
 * a card off, not a table you read. The only structural difference is what the
 * cards ARE. Upgrades are filed by the grade being applied for, so its hub is
 * one card per category; entry applications are filed by the DIET they were
 * submitted into — the sitting is the cycle here, and there is no separate
 * review-cycle entity to open — so this is one card per diet, and the category
 * cards are the screen behind it.
 */

type DietStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "active"
  | "assessment_ongoing"
  | "closed";

const STATUS_STYLE: Record<DietStatus, string> = {
  pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
  approved: "bg-blue-50 text-blue-700 border-blue-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  active: "bg-green-50 text-green-700 border-green-200",
  assessment_ongoing: "bg-purple-50 text-purple-700 border-purple-200",
  closed: "bg-gray-100 text-gray-600 border-gray-200",
};

const STATUS_LABEL: Record<DietStatus, string> = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  active: "Running",
  assessment_ongoing: "Assessment ongoing",
  closed: "Closed",
};

function asStatus(x: any): DietStatus {
  const s = String(x || "").toLowerCase();
  return (s in STATUS_STYLE ? s : "pending") as DietStatus;
}

function Badge({ status }: { status: DietStatus }) {
  return (
    <span
      className={`inline-flex px-2 py-1 text-[11px] font-bold rounded-full border ${STATUS_STYLE[status]}`}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function fmtDate(d?: string | null) {
  if (!d) return null;
  const dt = new Date(d);
  return isNaN(dt.getTime())
    ? String(d)
    : dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * One diet.
 *
 * Each card reads its own count. That is one small grouped query per diet
 * rather than one listing of every entry application ever submitted — the
 * cheaper shape here, and a bounded one: the page cannot grow a query that
 * scans the whole table as the years accumulate.
 */
function DietCard({ diet }: { diet: LogbookDiet }) {
  const status = asStatus((diet as any).status);
  const live = status === "active" || !!diet.is_active;

  const { data } = useGetDietCategoryCountsQuery({ dietId: diet.id });
  const count = data?.total;

  const range = [fmtDate(diet.start_date), fmtDate(diet.end_date)]
    .filter(Boolean)
    .join(" – ");

  return (
    <Link
      to={`/probationer-applications/admin/submissions/diets/${diet.id}/categories`}
      state={{ dietId: diet.id, dietTitle: diet.title }}
      className={`group relative bg-white border rounded-[32px] p-8 transition-all hover:shadow-2xl hover:shadow-gray-200 hover:-translate-y-1 overflow-hidden ${
        live ? "border-emerald-200 ring-1 ring-emerald-100" : "border-gray-200"
      }`}
    >
      <div
        className={`absolute top-0 right-0 w-24 h-24 rounded-bl-[60px] -mr-8 -mt-8 transition-colors ${
          live ? "bg-emerald-50" : "bg-gray-50 group-hover:bg-blue-50"
        }`}
      />
      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-start justify-between mb-6">
          <div
            className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm transition-all ${
              live
                ? "bg-emerald-600 text-white"
                : "bg-gray-100 group-hover:bg-blue-600 group-hover:text-white"
            }`}
          >
            <FolderOpen className="w-6 h-6" />
          </div>
          <div className="text-right">
            <div className="text-3xl font-black text-gray-900 leading-none tabular-nums">
              {count ?? "—"}
            </div>
            <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-1">
              Application{count === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-2">
          <h3 className="text-xl font-black text-gray-900 tracking-tight group-hover:text-blue-600 transition-colors">
            {diet.title}
          </h3>
          <Badge status={status} />
          {/* The running diet is the one new applications land in, and the one
              whose backlog the next diet will inherit — worth saying on the
              card rather than leaving it to the status word. */}
          {live && (
            <span className="inline-flex px-2 py-1 text-[11px] font-bold rounded-full border bg-emerald-600 text-white border-emerald-600">
              Current
            </span>
          )}
        </div>

        <p className="text-sm font-medium text-gray-500 leading-relaxed">
          {range || "No dates set"}
        </p>
        {diet.notes ? (
          <p className="text-xs text-gray-400 mt-1 line-clamp-2">{diet.notes}</p>
        ) : null}

        <div className="mt-auto pt-6 border-t border-gray-50 flex items-center justify-between">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 group-hover:text-gray-900 transition-colors">
            Enter Diet
          </span>
          <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all">
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function AdminProbationerApplicationList() {
  const { data = [], isLoading, isFetching, error, refetch } = useGetDietsQuery();

  const [q, setQ] = useState("");
  const [onlyOpen, setOnlyOpen] = useState(false);

  // Newest first, but the diet that is actually running leads regardless of
  // when it was created — it is where every day's work is.
  const sorted = useMemo(() => {
    const rank = (d: LogbookDiet) =>
      asStatus((d as any).status) === "active" || d.is_active ? 0 : 1;

    return [...(Array.isArray(data) ? data : [])].sort((a, b) => {
      const byLive = rank(a) - rank(b);
      if (byLive !== 0) return byLive;
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });
  }, [data]);

  const active = useMemo(
    () => sorted.find((d) => asStatus((d as any).status) === "active" || d.is_active),
    [sorted],
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return sorted.filter((d) => {
      if (onlyOpen && asStatus((d as any).status) === "closed") return false;
      if (!s) return true;
      return `${d.title ?? ""} ${d.notes ?? ""}`.toLowerCase().includes(s);
    });
  }, [sorted, q, onlyOpen]);

  return (
    <PageGate perm={PERMS.application_diet.access}>
      <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6">
        {/* Title row */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white border border-gray-100 p-6 rounded-[32px] shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-blue-50 border border-blue-100 rounded-2xl flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-blue-600" />
            </div>
            <div className="space-y-1">
              <h1 className="text-3xl font-black text-gray-900 tracking-tight">
                Entry Applications
              </h1>
              <p className="text-sm font-bold text-gray-500">
                Choose a diet to review the applications submitted into it.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 self-start">
            {/* Assignment is worked inside a diet, so this goes straight to the
                running one. Without a diet running there is nothing to assign,
                and the link is left out rather than dangled. */}
            {active && (
              <Link
                to={`/probationer-applications/admin/submissions/diets/${active.id}/assignments`}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-bold rounded-xl hover:bg-gray-50 transition-all shadow-sm"
              >
                <Users className="w-4 h-4" />
                Review Assignment
              </Link>
            )}
            <AccessGate perm="application.analysis.access">
              <Link
                to="/probationer-applications/analysis"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-bold rounded-xl hover:bg-gray-50 transition-all shadow-sm"
              >
                <BarChart3 className="w-4 h-4" />
                Analysis
              </Link>
            </AccessGate>
            {/* Diets themselves are created, approved and opened on the diet
                screen — this section reviews what is in them, it does not run
                them. Linking there beats duplicating those controls here.
                Gated on the ROLE as well as the privilege because that screen's
                own route is admin-only: offering a link that bounces whoever
                follows it is worse than not offering one. */}
            <AccessGate perm={PERMS.application_diet.activate} roles="admin">
              <Link
                to="/application-diets"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-sm"
              >
                <CalendarRange className="w-4 h-4" />
                Manage Diets
              </Link>
            </AccessGate>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-2.5 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 transition-all shadow-sm"
            >
              <RefreshCw
                className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`}
              />
            </button>
          </div>
        </div>

        {/* Filters — a closed diet is still worth opening (its record lives
            there), so they are hidden behind a toggle rather than dropped. */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[14rem] max-w-md">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search diets…"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={onlyOpen}
              onChange={() => setOnlyOpen((v) => !v)}
            />
            Hide closed diets
          </label>
        </div>

        {/* Diet grid */}
        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-64 bg-gray-50 border border-gray-100 rounded-[32px] animate-pulse"
              />
            ))}
          </div>
        ) : error ? (
          <div className="py-16 bg-rose-50 border border-rose-100 rounded-[32px] text-center">
            <p className="font-bold text-rose-700">Failed to load diets.</p>
            <button
              onClick={() => refetch()}
              className="mt-3 px-4 py-2 rounded-xl border border-rose-200 bg-white text-sm font-bold text-rose-700 hover:bg-rose-50"
            >
              Try again
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 bg-gray-50 border-2 border-dashed border-gray-200 rounded-[40px] flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-white rounded-3xl flex items-center justify-center shadow-sm mb-4">
              <LayoutGrid className="w-8 h-8 text-gray-300" />
            </div>
            <h2 className="text-xl font-black text-gray-900 uppercase">No diets</h2>
            <p className="text-gray-500 max-w-xs mx-auto mt-2 font-medium">
              {sorted.length === 0
                ? "No diet has been created yet."
                : "No diet matches your filters."}
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((d) => (
              <DietCard key={d.id} diet={d} />
            ))}
          </div>
        )}

        {/* Info footer */}
        <div className="mt-6 p-8 bg-blue-50/50 rounded-[32px] border border-blue-100 flex items-start gap-4">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm shrink-0">
            <Layers className="text-blue-600 w-5 h-5" />
          </div>
          <div>
            <h4 className="text-xs font-black text-blue-900 uppercase tracking-widest mb-1">
              Administrative Note
            </h4>
            <p className="text-xs font-bold text-blue-700/70 leading-relaxed max-w-2xl">
              Entry applications are grouped by the diet they were submitted into, then by the
              category applied for. Opening a new diet carries every application the committee
              never reported on into it, so nothing is stranded in a closed sitting.
            </p>
          </div>
        </div>
      </div>
    </PageGate>
  );
}
